import { defineMessages, useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMixpanel } from '@lifeomic/phc-web-toolkit/src/hooks/useMixpanel';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import { client } from '../../util/api';
import {
  memberOpsRequestErrorMessage,
  type MemberContactField,
} from './memberContactEdit';
import {
  getContactSyncIssue,
  MEMBER_CONTACT_SYNC_QUERY_KEY,
  type MemberContactSyncStatus,
  memberContactSyncStatusPath,
  type SyncContactVariables,
  syncMemberContactPath,
} from './memberContactSync';

const messages = defineMessages({
  syncSuccess: {
    id: 'MemberInfo.syncSuccess',
    defaultMessage: 'App login updated to match the Elation value.',
  },
  verifySuccess: {
    id: 'MemberInfo.verifySuccess',
    defaultMessage: 'App login marked verified.',
  },
  syncError: {
    id: 'MemberInfo.syncError',
    defaultMessage: 'Unable to sync. Try again.',
  },
});

type UseMemberContactSyncOptions = {
  enabled: boolean;
  onSynced?: () => void | Promise<void>;
};

/**
 * Loads a member's contact sync status and exposes `sync(field)`, which
 * overwrites the app login with the Elation value (or marks it verified),
 * notifies, tracks `member_contact_sync_completed`, and refetches the status.
 * `onSynced` runs after a successful sync.
 */
export const useMemberContactSync = (
  patientId: string | undefined,
  { enabled, onSynced }: UseMemberContactSyncOptions,
): {
  status?: MemberContactSyncStatus;
  sync: (field: MemberContactField) => void;
  syncingField?: MemberContactField;
} => {
  const [notify] = useNotify();
  const { track } = useMixpanel();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();

  const contactSyncQuery = useQuery({
    queryKey: [...MEMBER_CONTACT_SYNC_QUERY_KEY, patientId],
    queryFn: async (): Promise<MemberContactSyncStatus> => {
      const response = await client.get<MemberContactSyncStatus>(
        memberContactSyncStatusPath(patientId as string),
      );
      return response.data;
    },
    enabled,
  });

  const syncMutation = useMutation({
    mutationFn: async ({ field }: SyncContactVariables) => {
      await client.post(syncMemberContactPath(patientId as string), { field });
    },
    onSuccess: async (_data, { field, issue }) => {
      notify(
        formatMessage(
          issue === 'unverified'
            ? messages.verifySuccess
            : messages.syncSuccess,
        ),
        'success',
      );
      await queryClient.invalidateQueries({
        queryKey: MEMBER_CONTACT_SYNC_QUERY_KEY,
      });
      await onSynced?.();
      track('member_contact_sync_completed', { field, outcome: 'success' });
    },
    onError: (err: unknown, { field }) => {
      track('member_contact_sync_completed', { field, outcome: 'error' });
      notify(
        memberOpsRequestErrorMessage(err) ?? formatMessage(messages.syncError),
        'error',
      );
    },
  });

  const status = contactSyncQuery.data;
  return {
    status,
    sync: field => {
      if (!status) return;
      syncMutation.mutate({
        field,
        issue: getContactSyncIssue(status[field]),
      });
    },
    syncingField: syncMutation.isLoading
      ? syncMutation.variables?.field
      : undefined,
  };
};
